<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import PageHeader from '../../SettingsSubPageHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const inboxName = ref('');
const isCreating = ref(false);
const qrCodeBase64 = ref('');
const createdInbox = ref(null);
const createdInstanceName = ref('');
const isQrCodeRead = ref(false);
const pollInterval = ref(null);

const checkConnectionStatus = async () => {
  if (!createdInstanceName.value || isQrCodeRead.value) return;
  
  try {
    const response = await window.axios.get(`/api/v1/accounts/${accountId.value}/evolution`);
    const instances = response.data;
    const instance = instances.find(i => (i.instance?.instanceName || i.name || i.instanceName) === createdInstanceName.value);
    
    if (instance && instance.connectionStatus === 'open') {
      isQrCodeRead.value = true;
      if (pollInterval.value) {
        clearInterval(pollInterval.value);
        pollInterval.value = null;
      }
      
      // Finally, configure Chatwoot integration only AFTER it's fully connected
      await window.axios.post(`/api/v1/accounts/${accountId.value}/evolution/${createdInstanceName.value}/configure_integration`);
    } else {
      // Fetch the latest QR code to prevent it from expiring on screen
      const qrResponse = await window.axios.get(`/api/v1/accounts/${accountId.value}/evolution/${createdInstanceName.value}/connect`);
      if (qrResponse.data?.base64) {
        qrCodeBase64.value = qrResponse.data.base64;
      } else if (qrResponse.data?.qrcode?.base64) {
        qrCodeBase64.value = qrResponse.data.qrcode.base64;
      }
    }
  } catch (error) {
    // Ignore polling errors
  }
};

onBeforeUnmount(() => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value);
  }
});

const createEvolutionInbox = async () => {
  if (!inboxName.value.trim()) {
    useAlert(t('INBOX_MGMT.ADD.API_CHANNEL.INBOX_NAME_REQUIRED_ERROR'));
    return;
  }

  try {
    isCreating.value = true;
    const response = await window.axios.post(`/api/v1/accounts/${accountId.value}/evolution`, {
      inbox_name: inboxName.value,
    });
    
    createdInbox.value = response.data.inbox;
    qrCodeBase64.value = response.data.qr_code;
    createdInstanceName.value = response.data.instance_name;
    
    useAlert('Integração com Evolution criada com sucesso! Leia o QR Code.');
    
    pollInterval.value = setInterval(checkConnectionStatus, 3000);
  } catch (error) {
    useAlert(error.response?.data?.error || t('INBOX_MGMT.ADD.API_CHANNEL.ERROR_MESSAGE'));
  } finally {
    isCreating.value = false;
  }
};

const finishSetup = () => {
  router.push({
    name: 'settings_inboxes_add_agents',
    params: {
      page: 'new',
      inbox_id: createdInbox.value.id,
    },
  });
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="'WhatsApp (Evolution)'"
      :header-content="'Conecte o seu número de WhatsApp ao Chatwoot através do Evolution API lendo o QR Code.'"
    />
    <div class="mx-auto max-w-lg mt-8 p-6 bg-n-solid-1 border border-n-weak rounded-xl shadow-sm">
      <div v-if="!qrCodeBase64">
        <div class="mb-4">
          <Input
            v-model="inboxName"
            :label="'Nome da Caixa de Entrada'"
            :placeholder="'Ex: WhatsApp Vendas'"
            :disabled="isCreating"
            @keyup.enter="createEvolutionInbox"
          />
        </div>
        
        <div class="mt-6 flex justify-end">
          <Button
            :label="'Criar e Gerar QR Code'"
            :is-loading="isCreating"
            :disabled="isCreating || !inboxName"
            @click="createEvolutionInbox"
          />
        </div>
      </div>
      
      <div v-else class="text-center">
        <div v-if="!isQrCodeRead">
          <h3 class="text-heading-3 mb-4 text-n-slate-12">Leia o QR Code abaixo com seu WhatsApp</h3>
          
          <div class="p-4 bg-white border border-n-weak rounded-xl shadow-sm inline-block mx-auto mb-6">
            <img 
              :src="qrCodeBase64.startsWith('data:') ? qrCodeBase64 : `data:image/png;base64,${qrCodeBase64}`" 
              alt="WhatsApp QR Code" 
              class="w-64 h-64"
            />
          </div>
          
          <p class="text-body-main text-n-slate-11 mb-6">
            Após a leitura do QR Code, o seu WhatsApp estará conectado e você poderá receber mensagens.
          </p>
        </div>
        
        <div v-else>
          <div class="w-16 h-16 rounded-full bg-green-500/10 text-green-500 flex items-center justify-center mx-auto mb-4">
            <i class="i-lucide-check text-3xl"></i>
          </div>
          <h3 class="text-heading-3 mb-2 text-n-slate-12">Conectado com sucesso!</h3>
          <p class="text-body-main text-n-slate-11 mb-6">
            O seu WhatsApp foi conectado e já está pronto para uso.
          </p>
        </div>
        
        <div class="mt-4 flex justify-center">
          <Button
            :label="'Continuar para Adicionar Agentes'"
            @click="finishSetup"
          />
        </div>
      </div>
    </div>
  </div>
</template>
